import { useState } from 'react';
import { X, ChevronDown, Calendar } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import {
  FUNNEL_STATUS, EVENT_PLACE_LABEL, EVENT_PLACE_EMOJI, SCREEN_TYPE_LABEL, DEAL_TO_FUNNEL, LANGUAGE_OPTIONS,
  type Deal, type FunnelStatus, type EventPlace, type ScreenType, type GameFormat, type Source,
} from '@/types';

export interface GameFilter {
  timeFrom?: string; timeTo?: string;
  saleFrom?: string; saleTo?: string;
  status: FunnelStatus[];
  formats: string[];
  address?: string;
  places: EventPlace[];
  age?: string;
  playedBefore?: string;
  occasion?: string;
  languages: string[];
  prepayment?: string;
  screens: ScreenType[];
  distanceFee?: string;
  extraServices?: string;
  extraServicesSum?: string;
  count?: string;
  sources: string[];
}

export const EMPTY_FILTER: GameFilter = {
  status: [], formats: [], places: [], languages: [], screens: [], sources: [],
};

/** Сколько условий активно (для бейджа на кнопке «Фильтр»). */
export function filterCount(f: GameFilter): number {
  let n = 0;
  for (const v of Object.values(f)) {
    if (Array.isArray(v)) n += v.length ? 1 : 0;
    else if (v) n += 1;
  }
  return n;
}

/** Применить фильтр к списку сделок. */
export function applyGameFilter(deals: Deal[], f: GameFilter): Deal[] {
  const dt = (s?: string) => (s ? new Date(s).getTime() : undefined);
  const tf = dt(f.timeFrom), tt = dt(f.timeTo), sf = dt(f.saleFrom), st = dt(f.saleTo);
  const has = (s: string | undefined, q?: string) => !q || (s ?? '').toLowerCase().includes(q.toLowerCase());
  return deals.filter((d) => {
    const e = d.event;
    if (tf != null && (e.start_at ?? 0) < tf) return false;
    if (tt != null && (e.start_at ?? Infinity) > tt) return false;
    if (sf != null && d.created_at < sf) return false;
    if (st != null && d.created_at > st) return false;
    if (f.status.length && !f.status.includes(d.funnel_status ?? DEAL_TO_FUNNEL[d.status])) return false;
    if (f.formats.length && !(e.game_format_id && f.formats.includes(e.game_format_id))) return false;
    if (f.places.length && !(e.place && f.places.includes(e.place))) return false;
    // Язык теперь мультизначный («Казахский, Русский»): сделка проходит, если хотя бы один
    // из её языков попал в выбранные фильтром.
    if (f.languages.length) {
      const dealLangs = (e.language ?? '').split(',').map((s) => s.trim()).filter(Boolean);
      if (!dealLangs.some((l) => f.languages.includes(l))) return false;
    }
    if (f.screens.length && !(e.screen_type && f.screens.includes(e.screen_type))) return false;
    if (f.sources.length && !(d.source_id && f.sources.includes(d.source_id))) return false;
    if (!has(e.address, f.address)) return false;
    if (!has(e.participant_age_text, f.age)) return false;
    if (f.playedBefore && (e.played_before ? 'да' : 'нет') !== f.playedBefore.toLowerCase()) return false;
    if (!has(e.occasion_details, f.occasion)) return false;
    if (!has(e.prepayment_note, f.prepayment)) return false;
    if (f.distanceFee && String(e.extra_distance_fee ?? '') !== f.distanceFee) return false;
    if (!has((e.services ?? []).join(' '), f.extraServices)) return false;
    if (f.extraServicesSum && String(e.extra_services_amount ?? '') !== f.extraServicesSum) return false;
    if (!has(e.participant_count_text, f.count)) return false;
    return true;
  });
}

// ── Модалка фильтра ──
export function GamesFilterModal({ initial, formats, sources, onApply, onClose }: {
  initial: GameFilter; formats: GameFormat[]; sources: Source[];
  onApply: (f: GameFilter) => void; onClose: () => void;
}) {
  const [f, setF] = useState<GameFilter>(initial);
  const set = <K extends keyof GameFilter>(k: K, v: GameFilter[K]) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[920px] max-h-[92vh] overflow-y-auto panel shadow-pop animate-pop p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="flex-1 text-center text-xl font-bold text-ink-1">Фильтр</h2>
          <button className="icon-btn !w-9 !h-9 absolute right-4 top-4" onClick={onClose}><X className="w-5 h-5 text-accent" /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-start">
          <DateField label="Время игры от" value={f.timeFrom} onChange={(v) => set('timeFrom', v)} />
          <DateField label="Время игры до" value={f.timeTo} onChange={(v) => set('timeTo', v)} />
          <DateField label="Дата продажи от" value={f.saleFrom} onChange={(v) => set('saleFrom', v)} />

          <DateField label="Дата продажи до" value={f.saleTo} onChange={(v) => set('saleTo', v)} />
          <Multi label="Статус заказа" options={FUNNEL_STATUS.map((s) => ({ value: s.key, label: s.label, dot: s.color }))} selected={f.status} onChange={(v) => set('status', v as FunnelStatus[])} />
          <Multi label="Формат игры" options={formats.map((g) => ({ value: g.id, label: g.name, emoji: g.emoji }))} selected={f.formats} onChange={(v) => set('formats', v)} />

          <TextField label="Адрес" value={f.address} onChange={(v) => set('address', v)} />
          <Multi label="Место проведения" options={(Object.keys(EVENT_PLACE_LABEL) as EventPlace[]).map((p) => ({ value: p, label: EVENT_PLACE_LABEL[p], emoji: EVENT_PLACE_EMOJI[p] }))} selected={f.places} onChange={(v) => set('places', v as EventPlace[])} />
          <div className="hidden sm:block" />

          <TextField label="Возраст участников" value={f.age} onChange={(v) => set('age', v)} />
          <TextField label="Играли ранее" value={f.playedBefore} onChange={(v) => set('playedBefore', v)} />
          <TextField label="Повод" value={f.occasion} onChange={(v) => set('occasion', v)} />

          <Multi label="Язык" options={LANGUAGE_OPTIONS.map((l) => ({ value: l, label: l }))} selected={f.languages} onChange={(v) => set('languages', v)} />
          <TextField label="Предоплата" value={f.prepayment} onChange={(v) => set('prepayment', v)} />
          <Multi label="Экран на площадке" options={(Object.keys(SCREEN_TYPE_LABEL) as ScreenType[]).map((s) => ({ value: s, label: SCREEN_TYPE_LABEL[s] }))} selected={f.screens} onChange={(v) => set('screens', v as ScreenType[])} />

          <div className="hidden sm:block" />
          <TextField label="Доплата за дальность" value={f.distanceFee} onChange={(v) => set('distanceFee', v)} />
          <TextField label="Дополнительные услуги" value={f.extraServices} onChange={(v) => set('extraServices', v)} />

          <TextField label="Сумма дополнительных услуг" value={f.extraServicesSum} onChange={(v) => set('extraServicesSum', v)} />
          <TextField label="Кол-во участников" value={f.count} onChange={(v) => set('count', v)} />
          <Multi label="Откуда узнали" options={sources.map((s) => ({ value: s.id, label: s.name }))} selected={f.sources} onChange={(v) => set('sources', v)} />
        </div>

        <div className="flex items-center justify-center gap-3 mt-6">
          <button className="btn-ghost" onClick={() => setF(EMPTY_FILTER)}>Сбросить</button>
          <button className="btn-primary px-8" onClick={() => { onApply(f); onClose(); }}>Применить</button>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-ink-3 mb-0.5">{label}</span>
      <input className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={label} />
    </label>
  );
}

function DateField({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-ink-3 mb-0.5 flex items-center gap-1"><Calendar className="w-3 h-3" /> {label}</span>
      <DateTimePicker value={value ?? ''} onChange={onChange} />
    </label>
  );
}

function Multi({ label, options, selected, onChange, up }: {
  label: string; options: { value: string; label: string; emoji?: string; dot?: string }[];
  selected: string[]; onChange: (v: string[]) => void; up?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="relative">
      <span className="block text-[11px] text-ink-3 mb-0.5">{label}</span>
      <button className="input flex items-center justify-between text-left" onClick={() => setOpen((v) => !v)}>
        <span className="truncate text-ink-2">{selected.length ? `Выбрано: ${selected.length}` : ''}</span>
        <ChevronDown className="w-4 h-4 text-ink-3 flex-shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={cn('absolute z-50 left-0 w-full menu max-h-[260px] overflow-y-auto px-1', up ? 'bottom-full mb-1' : 'top-full mt-1')}>
            {options.map((o) => (
              <label key={o.value} className={cn('flex items-center gap-2 px-2 h-9 rounded-lg hover:bg-surface-2 cursor-pointer text-sm', selected.includes(o.value) && 'bg-accent-soft/40')}>
                <input type="checkbox" className="w-4 h-4 accent-accent" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                {o.dot && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: o.dot }} />}
                {o.emoji && <span>{o.emoji}</span>}
                <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
